import RollupFilters from "./rollup-filters";
import Field from "./field";
import Api from "./api";
import { arrayStartsWith, escapePostgre, normalize, replaceAll } from "../utils";
import IRollupReturnData from "../interface/rollup-return-data";
import IRollupFilter from "../interface/rollup-filter";
import IRollup from "../interface/rollup";
import IRelationPathField from "../interface/relation-path-field";
import IApiRollupPtDetail from "../interface/api/api-rollup-pt-detail";
import IApiRollup from "../interface/api/api-rollup";
import IApiField from "../interface/api/api-field";
import IApiEntity from "../interface/api/api-entity";
import RollupCalcType from "../enum/rollup-calc-type";
import FieldType from "../enum/field-type";
import ErrorCode from "../enum/error-code";
import RollupCacheItem from "../class/rollup-cache-item";

class RollupInstance {

    private LabelFieldTypes: FieldType[] = [FieldType.text, FieldType.check, FieldType.number, FieldType.datetime, FieldType.url, FieldType.phone];

    private ValueCache: RollupCacheItem[] = [];

    /* Public Methods */

    public async getData(id: number): Promise<IRollupReturnData> {
        const rollup: IApiRollup | null = Api.rollupGet(id);
        if (rollup) {
            let cache: RollupCacheItem | undefined = this.ValueCache.find((c: RollupCacheItem) => c.rollup.id === rollup.id);
            if (!cache) {
                cache = new RollupCacheItem(rollup);
                this.ValueCache.push(cache);
            }
            return await cache.getData();
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public getLabelFields(entityId: number): IRelationPathField[] {
        return Field.getRelationPathsFields(entityId, this.LabelFieldTypes, 0, true);
    }

    public getFilterFields(entityId: number, paths: string[] = []): IRelationPathField[] {
        const types: FieldType[] = RollupFilters.getTypes();
        const fields: IRelationPathField[] = Field.getRelationPathsFields(entityId, types, 0, true);
        if (paths.length) {
            return fields.filter((field: IRelationPathField) => {
                for (let i = 0; i < paths.length; i++) {
                    let path: number[] = paths[i].split(',').map((id: string) => Number(id));
                    path = path.slice(0, path.length - 1);
                    if (path.length && !arrayStartsWith(path, field.path) && !arrayStartsWith(field.path, path)) {
                        return false;
                    }
                }
                return true;
            });
        }
        return fields;
    }

    /* Private Methods */

    public get(id: number): IRollup | null {
        const raw: IApiRollup | null = Api.rollupGet(id);
        if (raw) {
            const result: IRollup = {
                id: raw.id as number,
                entityId: raw.rolPtDetails[raw.rolPtDetails.length - 1].rolECN as number,
                fieldId: raw.rolFCN || 0,
                calcType: raw.rolFunc,
                filters: RollupFilters.parseServerFilters(raw)
            };
            if (raw.rolGroups.length) {
                result.groupField = this.getGroupField(raw);
            }
            return result;
        }
        return null;
    }

    public async create(name: string, rollup: IRollup): Promise<IRollup> {
        if (this.valid(rollup)) {
            const apiRlp: Partial<IApiRollup> = escapePostgre(this.getApiFormat(rollup, name));
            const result: IApiRollup = await Api.rollupCreate(apiRlp);
            return this.get(result.id as number) as IRollup;
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async update(rollup: IRollup): Promise<IRollup> {
        const raw: IApiRollup | null = Api.rollupGet(rollup.id);
        if (raw && this.valid(rollup)) {
            Object.assign(raw, this.getApiFormat(rollup));
            await Api.fieldSave(raw);
            return this.get(rollup.id) as IRollup;
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async delete(id: number): Promise<number> {
        const raw: IApiRollup | null = Api.rollupGet(id);
        if (raw) {
            await Api.fieldDelete(raw);
        }
        return id;
    }

    private valid(rollup: IRollup): boolean {
        const entity: IApiEntity | null = Api.getEntity(rollup.entityId);
        if (!entity) {
            return false;
        }
        const field: IApiField | undefined = entity.fields.find((f: IApiField) => f.id === rollup.fieldId);
        if (rollup.calcType !== RollupCalcType.Count && !field) {
            return false;
        }
        if (rollup.groupField && !this.validPathField(rollup.groupField)) {
            return false;
        }
        for (let i = rollup.filters.length; i--;) {
            if (!this.validPathField(rollup.filters[i].fieldId)) {
                return false;
            }
        }
        return !!rollup.calcType;
    }

    /* Private Methods - Server-Client format data transform */

    private getApiFormat(rollup: IRollup, singular: string = ''): Partial<IApiRollup> {
        const result: Partial<IApiRollup> = {
            rolFunc: rollup.calcType,
            rolFCN: rollup.fieldId,
            rolGroups: this.getRolGroups(rollup.groupField),
            rolPtDetails: this.getPtDetail(rollup)
        };
        if (singular) {
            result.singular = `Global_${replaceAll(normalize(singular), ' ', '')}`;
        }
        RollupFilters.setServerFilters(rollup, result);
        return result;
    }

    private validPathField(path: string): boolean {
        const strPath: string[] = path.split(',');
        for (let i = strPath.length; i--;) {
            if (!Field.get(parseInt(strPath[i]))) {
                return false;
            }
        }
        return true;
    }

    /* Private Methods - Server-Client format data transform - Paths */

    private getPtDetail(rollup: IRollup): IApiRollupPtDetail[] {
        const result: IApiRollupPtDetail[] = [];
        const paths: number[][] = [];
        if (rollup.groupField) {
            paths.push(this.parsePathId(rollup.groupField));
        }
        rollup.filters.forEach((filter: IRollupFilter) => {
            paths.push(this.parsePathId(filter.fieldId));
        });
        if (paths.length) {
            const lengths: number[] = paths.map((p: number[]) => p.length);
            const longestIndex: number = lengths.indexOf(Math.max.apply(Math, lengths));
            paths[longestIndex].forEach((ptId: number) => {
                result.push({
                    where: '',
                    ptId
                });
            });
        }
        result.push({
            where: '',
            ptId: 0,
            rolECN: rollup.entityId
        });
        return result;
    }

    private parsePathId(path: string): number[] {
        const result: number[] = path.split(',').map((i: string) => parseInt(i));
        return result.slice(0, result.length - 1);
    }

    /* Private Methods - Server-Client format data transform - Groups */

    private getRolGroups(groupField: string | undefined): number[] {
        if (groupField) {
            const pathId: number[] = groupField.split(',').map((f: string) => Number(f));
            return [Number(pathId[pathId.length - 1])];
        }
        return [];
    }

    private getGroupField(raw: IApiRollup): string | undefined {
        if (raw.rolGroups.length) {
            const path: number[] | undefined = RollupFilters.getFieldRollupPath(raw, raw.rolGroups[0]);
            if (path) {
                return path.join(',');
            }
        }
    }

}

const Rollup = new RollupInstance();

export default Rollup;